import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, Save, X } from "lucide-react";

/**
 * Top-bar control to manage the OPTIONAL per-queue visitor limit.
 *
 * Visual : a card matching the other "Console de gestion" controls (rounded-2xl,
 * tinted bg, colored medallion + status pill + description). A larger ON/OFF
 * switch in the top-right toggles the limit.
 *
 * Behaviour :
 * - Default : switch OFF → unlimited file ("FILE ILLIMITÉE").
 * - Switch ON when no limit set → opens the modal so the user picks a number.
 *   If the user cancels, the switch flips back OFF (no change committed).
 * - Switch OFF when a limit was set → instantly clears it (suspendLimit=0).
 * - Tapping the body of the card (icon / title) when ON re-opens the modal so
 *   the user can edit the value without losing the limit.
 */
export default function QueueLimitControl({ queue, onChange }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const popoverRef = useRef(null);

  const currentLimit = queue?.suspendLimit || 0;
  const isActive = currentLimit > 0;

  useEffect(() => {
    if (open) {
      setEnabled(currentLimit > 0);
      setValue(currentLimit > 0 ? String(currentLimit) : "");
    }
  }, [open, currentLimit]);

  if (!queue?.id) return null;

  const persist = async (payload) => {
    setSaving(true);
    try {
      await api.patch(`/merchant/queues/${queue.id}`, { suspendLimit: payload });
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const handleSwitch = async () => {
    if (isActive) {
      // Switching OFF — clear the limit instantly, no modal needed.
      await persist(0);
      toast.success("Limite désactivée — file illimitée");
    } else {
      // Switching ON — open the modal so the user picks a max number.
      setOpen(true);
    }
  };

  const save = async () => {
    let payload = 0;
    if (enabled) {
      const n = parseInt(value, 10);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("Saisissez un nombre supérieur à 0");
        return;
      }
      if (n > 9999) {
        toast.error("Limite maximale : 9999");
        return;
      }
      payload = n;
    }
    await persist(payload);
    toast.success(payload > 0 ? `Limite définie : ${payload} visiteurs` : "Limite désactivée — file illimitée");
    setOpen(false);
  };

  return (
    <>
      <div
        className="relative flex flex-col items-center text-center rounded-2xl border-2 border-[#7B5CFF]/40 bg-[#7B5CFF]/5 p-2.5 sm:p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => setOpen(true)}
        data-testid="queue-limit-card"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
      >
        {/* Big switch top-right — stops propagation so the card click doesn't also fire */}
        <button
          type="button"
          role="switch"
          aria-checked={isActive}
          aria-label={isActive ? "Désactiver la limite" : "Activer la limite"}
          onClick={(e) => { e.stopPropagation(); handleSwitch(); }}
          data-testid="queue-limit-switch"
          className="absolute top-3 right-3 inline-flex items-center h-6 w-11 sm:h-7 sm:w-12 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#7B5CFF]/50"
          style={{ backgroundColor: isActive ? "#7B5CFF" : "#D1D5DB" }}
        >
          <span
            aria-hidden="true"
            className={`inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white shadow transition-transform ${isActive ? "translate-x-[22px]" : "translate-x-0.5"}`}
          />
        </button>

        <span aria-hidden="true" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#7B5CFF]/15 text-[#7B5CFF] inline-flex items-center justify-center mb-2 sm:mb-3">
          <Users size={22} />
        </span>
        <h3 className="font-display font-black uppercase tracking-[0.06em] text-sm sm:text-base text-ink leading-tight" data-testid="queue-limit-top-btn">
          Limite de file
        </h3>
        <span className="mt-2 inline-block px-3 py-1 rounded-md text-[11px] font-black uppercase tracking-wider bg-[#7B5CFF]/15 text-[#7B5CFF]" data-testid="queue-limit-pill">
          {isActive ? `Limite : ${currentLimit}` : "File illimitée"}
        </span>
        <p className="mt-2 text-[11px] text-subtle leading-snug">
          {isActive ? "Suspension auto. à la limite" : "Aucune limite définie"}
        </p>
      </div>

      {/* Modal — same content as before, just centered */}
      {open && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="queue-limit-overlay"
        >
          <div
            ref={popoverRef}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm mx-auto bg-white border-2 border-border rounded-xl shadow-2xl overflow-hidden"
            data-testid="queue-limit-popover"
          >
            <div className="px-4 py-3 border-b border-border bg-surface/60 flex items-center justify-between">
              <p className="overline inline-flex items-center gap-2"><Users size={14} /> Limite de file (optionnelle)</p>
              <button onClick={() => setOpen(false)} className="text-subtle hover:text-ink" data-testid="queue-limit-close-btn">
                <X size={16} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#7B5CFF] cursor-pointer"
                  data-testid="queue-limit-enable-toggle"
                />
                <div className="flex-1">
                  <p className="text-sm font-bold text-ink">{enabled ? "Limite activée" : "Aucune limite — file illimitée"}</p>
                  <p className="text-xs text-subtle mt-0.5">
                    {enabled
                      ? "Quand le nombre de visiteurs en attente atteint la limite, la file se suspend automatiquement et bloque les nouveaux tickets jusqu'à libération d'une place."
                      : "Par défaut. Aucun blocage automatique. Les visiteurs peuvent prendre un ticket sans restriction."}
                  </p>
                </div>
              </label>

              {enabled && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-subtle">Nombre maximum de visiteurs</label>
                  <input
                    type="number"
                    min="1"
                    max="9999"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Ex : 10"
                    autoFocus
                    className="mt-1 w-full border border-border h-10 px-2 text-base font-mono rounded-md"
                    data-testid="queue-limit-input"
                  />
                  <p className="text-[10px] text-subtle mt-1">Modifiable à tout moment. Ne réinitialise pas la file.</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-center h-9 px-3 border border-border text-subtle hover:bg-surface text-xs font-bold rounded-md uppercase tracking-wider"
                  data-testid="queue-limit-cancel-btn"
                >
                  Annuler
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-1.5 h-9 px-4 bg-ink text-white text-xs font-bold disabled:opacity-50 rounded-md uppercase tracking-wider"
                  data-testid="queue-limit-save-btn"
                >
                  <Save size={12} /> {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
